import { supabase } from './supabase';

export type IntakeFieldType =
  | 'short_text'
  | 'long_text'
  | 'email'
  | 'phone'
  | 'number'
  | 'single_select'
  | 'checkbox'
  | 'photo';

export interface IntakeFormTemplate {
  id: string;
  business_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface IntakeFormTemplateSection {
  id: string;
  template_id: string;
  title: string;
  sort_order: number;
  created_at?: string;
}

export interface IntakeFormTemplateField {
  id: string;
  template_id: string;
  section_id: string;
  sort_order: number;
  field_type: IntakeFieldType;
  label: string;
  is_required: boolean;
  options: string[];
}

export interface IntakeFieldDraft {
  field_type: IntakeFieldType;
  label: string;
  is_required: boolean;
  /** newline-separated choices for single_select */
  optionsText: string;
}

/** One titled section and its fields (design-time). */
export interface IntakeSectionDraft {
  title: string;
  fields: IntakeFieldDraft[];
}

export type IntakeTemplateWithSections = {
  template: IntakeFormTemplate;
  sections: Array<{
    section: IntakeFormTemplateSection;
    fields: IntakeFormTemplateField[];
  }>;
};

function optionsFromDraft(fieldType: IntakeFieldType, optionsText: string): string[] {
  if (fieldType === 'single_select') {
    return optionsText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (fieldType === 'photo') {
    const n = parseInt(String(optionsText).trim(), 10);
    const clamped = !Number.isFinite(n) || n < 1 ? 5 : Math.min(20, Math.max(1, n));
    return [String(clamped)];
  }
  return [];
}

export async function listIntakeFormTemplates(businessId: string): Promise<IntakeFormTemplate[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data, error } = await supabase
    .from('crm_intake_form_templates')
    .select('id, business_id, title, created_at, updated_at')
    .eq('business_id', businessId)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return (data || []) as IntakeFormTemplate[];
}

function normalizeField(row: any): IntakeFormTemplateField {
  return {
    id: row.id,
    template_id: row.template_id,
    section_id: row.section_id,
    sort_order: row.sort_order,
    field_type: row.field_type as IntakeFieldType,
    label: row.label,
    is_required: Boolean(row.is_required),
    options: Array.isArray(row.options) ? row.options.map(String) : [],
  };
}

export async function getIntakeTemplateWithFields(
  templateId: string,
): Promise<IntakeTemplateWithSections | null> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { data: template, error: tErr } = await supabase
    .from('crm_intake_form_templates')
    .select('id, business_id, title, created_at, updated_at')
    .eq('id', templateId)
    .maybeSingle();

  if (tErr) throw tErr;
  if (!template) return null;

  const { data: sectionRows, error: sErr } = await supabase
    .from('crm_intake_form_template_sections')
    .select('id, template_id, title, sort_order, created_at')
    .eq('template_id', templateId)
    .order('sort_order', { ascending: true });

  if (sErr) throw sErr;

  const { data: fieldRows, error: fErr } = await supabase
    .from('crm_intake_form_template_fields')
    .select('id, template_id, section_id, sort_order, field_type, label, is_required, options')
    .eq('template_id', templateId)
    .order('sort_order', { ascending: true });

  if (fErr) throw fErr;

  const sections = (sectionRows || []).map((row: any) => ({
    section: {
      id: row.id,
      template_id: row.template_id,
      title: row.title,
      sort_order: row.sort_order,
      created_at: row.created_at,
    } as IntakeFormTemplateSection,
    fields: [] as IntakeFormTemplateField[],
  }));

  const bySection = new Map<string, IntakeFormTemplateField[]>();
  for (const s of sections) {
    bySection.set(s.section.id, s.fields);
  }

  for (const row of fieldRows || []) {
    const f = normalizeField(row);
    const bucket = bySection.get(f.section_id);
    if (bucket) bucket.push(f);
  }

  return { template: template as IntakeFormTemplate, sections };
}

export async function saveIntakeTemplate(args: {
  businessId: string;
  templateId?: string | null;
  title: string;
  /** At least one section required; fields live inside sections. */
  sections: IntakeSectionDraft[];
}): Promise<{ templateId: string }> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const title = args.title.trim();
  if (!title) throw new Error('Title is required');
  if (!args.sections.length) throw new Error('Add at least one section');

  let templateId = args.templateId || null;

  if (!templateId) {
    const { data: inserted, error: insErr } = await supabase
      .from('crm_intake_form_templates')
      .insert({ business_id: args.businessId, title })
      .select('id')
      .single();
    if (insErr) throw insErr;
    templateId = inserted.id;
  } else {
    const { error: upErr } = await supabase
      .from('crm_intake_form_templates')
      .update({ title, updated_at: new Date().toISOString() })
      .eq('id', templateId);
    if (upErr) throw upErr;

    const { error: delFields } = await supabase
      .from('crm_intake_form_template_fields')
      .delete()
      .eq('template_id', templateId);
    if (delFields) throw delFields;

    const { error: delSec } = await supabase
      .from('crm_intake_form_template_sections')
      .delete()
      .eq('template_id', templateId);
    if (delSec) throw delSec;
  }

  const sectionIds: string[] = [];
  for (let index = 0; index < args.sections.length; index++) {
    const s = args.sections[index];
    const { data: secRow, error: oneSecErr } = await supabase
      .from('crm_intake_form_template_sections')
      .insert({
        template_id: templateId!,
        title: s.title.trim() || `Section ${index + 1}`,
        sort_order: index,
      })
      .select('id')
      .single();
    if (oneSecErr) throw oneSecErr;
    if (!secRow?.id) throw new Error('Failed to save section');
    sectionIds.push(secRow.id);
  }

  const fieldRows: Array<{
    template_id: string;
    section_id: string;
    sort_order: number;
    field_type: IntakeFieldType;
    label: string;
    is_required: boolean;
    options: string[];
  }> = [];

  let sortOrder = 0;
  for (let si = 0; si < args.sections.length; si++) {
    const sectionId = sectionIds[si];
    if (!sectionId) continue;
    const secDraft = args.sections[si];
    for (const f of secDraft.fields) {
      fieldRows.push({
        template_id: templateId!,
        section_id: sectionId,
        sort_order: sortOrder++,
        field_type: f.field_type,
        label: f.label.trim() || 'Untitled field',
        is_required: f.is_required,
        options: optionsFromDraft(f.field_type, f.optionsText),
      });
    }
  }

  if (fieldRows.length > 0) {
    const { error: fieldErr } = await supabase.from('crm_intake_form_template_fields').insert(fieldRows);
    if (fieldErr) throw fieldErr;
  }

  return { templateId: templateId! };
}

export async function deleteIntakeFormTemplate(templateId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  await supabase
    .from('crm_intake_form_instances')
    .delete()
    .eq('template_id', templateId)
    .eq('is_template_preview', true);
  const { error } = await supabase.from('crm_intake_form_templates').delete().eq('id', templateId);
  if (error) throw error;
}
